import { schemaQuerySnapshotQueryOptions } from '@valian/react-kitchen-sink'

import { collections } from '../../schema'
import { schemaQueryReadQueryOptions } from '../queryOptions'

export const regionsQuery = () =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['regions'],
    factory: collections.regions,
    query: {
      name: `All regions`,
      orderBy: [['name', 'asc']],
    },
  })

export const operatingRegionsQuery = () =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['regions', 'operating'],
    factory: collections.regions,
    query: {
      name: `Operating regions`,
      where: [['currentOperatingDate', '!=', null]],
    },
  })

/**
 * Query teams for an evening (suspense-compatible).
 * Uses snapshot query options for compatibility with useSuspenseQuery.
 * For historical evenings, the data won't change anyway.
 */
export const eveningTeamsQuery = (eveningId: number) =>
  schemaQuerySnapshotQueryOptions({
    queryKey: ['evening-teams', eveningId],
    factory: collections.teams,
    enabled: !!eveningId,
    query: {
      name: `Teams for evening ${eveningId}`,
      where: [['eveningId', '==', eveningId]],
    },
  })

/**
 * Query teams for an evening.
 * @param eveningId The evening ID to query teams for
 * @param isLive If true (default), uses a real-time snapshot listener. If false, does a one-time read.
 *               Use isLive=false for closed evenings where data won't change.
 */
export const operatingRegionTeamsQuery = (eveningId: number, isLive = true) =>
  isLive
    ? schemaQuerySnapshotQueryOptions({
        queryKey: ['operating-regions', 'teams', eveningId],
        factory: collections.teams,
        enabled: !!eveningId,
        query: {
          name: `Teams for evening ${eveningId}`,
          where: [['eveningId', '==', eveningId]],
        },
      })
    : schemaQueryReadQueryOptions({
        queryKey: ['operating-regions', 'teams', eveningId],
        factory: collections.teams,
        enabled: !!eveningId,
        query: {
          name: `Teams for evening ${eveningId}`,
          where: [['eveningId', '==', eveningId]],
        },
      })

/**
 * Query transport requests for the operating regions page.
 * @param eveningId The evening ID to query transport requests for
 * @param isLive If true (default), uses a real-time snapshot listener. If false, does a one-time read.
 *               Use isLive=false for closed evenings where data won't change.
 */
export const operatingRegionTransportRequestsQuery = (eveningId: number, isLive = true) =>
  isLive
    ? schemaQuerySnapshotQueryOptions({
        queryKey: ['operating-regions', 'transportRequests', eveningId],
        factory: collections.transportRequests,
        enabled: !!eveningId,
        query: {
          name: `Transport requests for evening ${eveningId}`,
          where: [
            ['eveningId', '==', eveningId],
            ['status', 'not-in', ['draft', 'deleted_draft', 'submitted', 'error']],
          ],
        },
      })
    : schemaQueryReadQueryOptions({
        queryKey: ['operating-regions', 'transportRequests', eveningId],
        factory: collections.transportRequests,
        enabled: !!eveningId,
        query: {
          name: `Transport requests for evening ${eveningId}`,
          where: [
            ['eveningId', '==', eveningId],
            ['status', 'not-in', ['draft', 'deleted_draft', 'submitted', 'error']],
          ],
        },
      })

/**
 * Query for transport requests for stats charts.
 * Includes 'submitted' status (newly received requests) in addition to confirmed/completed ones.
 * Unlike operatingRegionTransportRequestsQuery, this shows all requests that have been received,
 * not just those that have been confirmed by dispatch.
 *
 * @param eveningId The evening ID to query transport requests for
 * @param isLive If true (default), uses a real-time snapshot listener. If false, does a one-time read.
 *               Use isLive=false for closed evenings where data won't change.
 */
export const statsTransportRequestsQuery = (eveningId: number, isLive = true) =>
  isLive
    ? schemaQuerySnapshotQueryOptions({
        queryKey: ['stats', 'transportRequests', eveningId],
        factory: collections.transportRequests,
        enabled: !!eveningId,
        query: {
          name: `Stats transport requests for evening ${eveningId}`,
          where: [
            ['eveningId', '==', eveningId],
            ['status', 'not-in', ['draft', 'deleted_draft', 'error']],
          ],
        },
      })
    : schemaQueryReadQueryOptions({
        queryKey: ['stats', 'transportRequests', eveningId],
        factory: collections.transportRequests,
        enabled: !!eveningId,
        query: {
          name: `Stats transport requests for evening ${eveningId}`,
          where: [
            ['eveningId', '==', eveningId],
            ['status', 'not-in', ['draft', 'deleted_draft', 'error']],
          ],
        },
      })
